/**
 * Menú del Portal Alta Dirección, armado desde la base.
 *
 * Antes el sidebar era una lista fija en el layout: los nombres no eran los de
 * `submenus`, el orden tampoco, y un submenú nuevo (o renombrado) no aparecía hasta
 * tocar código. Aquí se leen los submenús reales — nombre, ruta y orden — y lo único
 * que decide el front es **qué ícono** le toca a cada ruta.
 *
 * Agrupación: sale de la propia ruta. Un submenú de un solo segmento
 * (`/admin/portal-alta-direccion/pipeline`) es un elemento suelto; los de dos
 * (`/admin/portal-alta-direccion/mediciones/portales`) se agrupan bajo su segmento
 * padre, colapsable. No hay tabla de grupos, así que no se inventa una.
 *
 * El permiso NO se resuelve aquí: el layout filtra con `useAllowedMenus`, que es el
 * mismo gate que autoriza las rutas.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const PREFIJO_ALTA_DIRECCION = "/admin/portal-alta-direccion/";

export interface SubmenuPortal {
  id: number;
  nombre: string;
  ruta: string;
  orden: number;
}

/** Submenús activos del portal, en el orden que dice la base. */
export function useSubmenusPortal(prefijo: string = PREFIJO_ALTA_DIRECCION) {
  return useQuery({
    queryKey: ["submenus-portal", prefijo],
    queryFn: async (): Promise<SubmenuPortal[]> => {
      const { data, error } = await supabase
        .from("submenus")
        .select("id, nombre, vista_front_end, orden, activo, menus!inner(activo)")
        .like("vista_front_end", `${prefijo}%`)
        .eq("activo", true)
        .order("orden", { ascending: true });

      if (error) {
        // Fallo cerrado en cuanto a datos, abierto en cuanto a UI: el layout muestra
        // el portal sin menú en vez de romperse.
        console.error("No se pudieron leer los submenús del portal:", error);
        return [];
      }

      return (data ?? [])
        .filter((s: any) => s.menus?.activo !== false && !!s.vista_front_end)
        .map((s: any) => ({
          id: s.id as number,
          nombre: (s.nombre ?? "").trim(),
          ruta: s.vista_front_end as string,
          orden: (s.orden ?? 100) as number,
        }));
    },
    staleTime: 5 * 60_000,
  });
}

/** Etiqueta de un grupo derivado del segmento de la ruta (`mediciones` → "Mediciones"). */
export function etiquetaSegmento(segmento: string): string {
  const limpio = segmento.replace(/[-_]/g, " ").trim();
  return limpio.charAt(0).toUpperCase() + limpio.slice(1);
}

/**
 * Parte la ruta en (segmento padre | null, resto). Solo se agrupa cuando el submenú
 * cuelga de un segmento intermedio: `mediciones/portales` → ("mediciones", "portales").
 */
export function segmentoPadre(ruta: string, prefijo: string = PREFIJO_ALTA_DIRECCION): string | null {
  if (!ruta.startsWith(prefijo)) return null;
  const partes = ruta.slice(prefijo.length).split("/").filter(Boolean);
  return partes.length > 1 ? partes[0] : null;
}
